// Crowd sound cues for the spectator hotspots (RALLY_GOAL.md §5 P1-6): how loud the murmur of the
// nearest crowd is, where it sits in the stereo field, and when it cheers (once per pass, re-armed
// after the car has left). Pure, so it is tested without an AudioContext.

/** The cheer starts this close (m); the crowd is ready again once the car is this far away. */
export const CHEER_RANGE = 70;
export const REARM_RANGE = 160;
/** The murmur fades in from this distance (m). */
export const MURMUR_RANGE = 200;

export class CrowdCue {
  /** 0…1 murmur level, −1…1 pan, and whether a cheer starts this frame (with its 0…1 level). */
  murmur = 0;
  pan = 0;
  cheer = false;
  cheerLevel = 0;
  private readonly armed: boolean[];

  constructor(private readonly sites: readonly { x: number; z: number }[]) {
    this.armed = sites.map(() => true);
  }

  /** Listener position and its right vector on the ground (x, z). Allocation-free. */
  update(x: number, z: number, rightX: number, rightZ: number): void {
    let best = -1;
    let bestD = Infinity;
    this.cheer = false;
    for (let k = 0; k < this.sites.length; k++) {
      const d = Math.hypot(this.sites[k].x - x, this.sites[k].z - z);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
      if (d > REARM_RANGE) this.armed[k] = true;
      else if (d < CHEER_RANGE && this.armed[k]) {
        this.armed[k] = false;
        this.cheer = true;
        this.cheerLevel = Math.min(1, 1.15 - d / (CHEER_RANGE * 1.6));
      }
    }
    if (best < 0) {
      this.murmur = 0;
      this.pan = 0;
      return;
    }
    const f = Math.max(0, 1 - bestD / MURMUR_RANGE);
    this.murmur = f * f;
    const dx = this.sites[best].x - x;
    const dz = this.sites[best].z - z;
    this.pan = Math.max(-1, Math.min(1, (dx * rightX + dz * rightZ) / Math.max(bestD, 1)));
  }
}
